import { IDataTable } from '@shared/models/data-table.model';
import { ElementCategoryID } from '@shared/models/element-categories.model';
import {
  ElementQuantityName,
  IElementQuantityProperty,
} from '@shared/models/element-quantities.model';
import {
  IElementProperty,
  IFactoryProperty,
} from '@shared/models/element-property.model';
import { ProductID, ProductRecord } from '@shared/models/product.model';
import {
  ElementOrigin,
  ExpressionValue,
  Baseline,
  IElement,
  OneOfChildElements,
} from '@shared/models/project.model';
import { IProposal, ProposalId } from '@shared/models/proposals.model';
import type { Recipe, RecipeID } from '@shared/models/recipe.model';
import { Results, ResultsRecord } from '@shared/models/unit.model';
import {
  IFactoryVersion,
  OneOfChildFactoryElements,
} from '@shared/models/factory-element.model';
import { SemiPartial } from '@shared/models/type-helpers.model';
import { IInsertSortPlacement } from '@shared/models/sort.model';
import { RegenerateIds } from '../id.helpers';
import { isOneOf } from '../array.helpers';

/** Discriminant enum for all project-level change payloads applied by the update engine */
export enum ProjectChangeType {
  // ── Element changes ─────────────────────────────────────────
  ElementPropertyResolved = 'element.property.resolved',
  ElementPropertyExpression = 'element.property.expression',
  ElementPropertyFormatted = 'element.property.formatted',
  /** User numeric override on an expression property ({@link ExpressionValue.value}) */
  ElementPropertyValue = 'element.property.value',
  ElementQuantityResolved = 'element.quantity.resolved',
  ElementQuantityExpression = 'element.quantity.expression',
  /**
   * Formula update on {@link IElementQuantityExpressionProperty.fallbackCount} (pipeline /
   * programmatic paths). User edits to quantity formulas use {@link ProjectChangeType.ElementQuantityExpression}
   * on `count` instead. Property-change conversion does not emit this type today; the update
   * handler exists for direct {@link ProjectChange} application and future callers.
   */
  ElementQuantityFallbackExpression = 'element.quantity.fallbackExpression',
  ElementQuantityFormatted = 'element.quantity.formatted',
  /** User numeric override on an expression quantity ({@link ExpressionValue.value}) */
  ElementQuantityValue = 'element.quantity.value',
  /** Set count on a quantity property (e.g. to clear it so fallbackCount takes effect) */
  ElementQuantityCount = 'element.quantity.count',
  /** Pipeline / enrichment: replace full {@link ExpressionValue} on a quantity row's `fallbackCount` */
  ElementQuantityFallbackCount = 'element.quantity.fallbackCount',
  /** Set fallbackCount on an element property (from inherited parent variables) */
  ElementPropertyFallbackCount = 'element.property.fallbackCount',
  /** Set count on an element property (from data table sync) */
  ElementPropertyCount = 'element.property.count',
  /** Rename an editable element property */
  ElementPropertyRename = 'element.property.rename',
  /** Replace data-table-linked properties and append properties from a data table */
  ElementDataTablePropertiesApply = 'element.dataTable.propertiesApply',
  /** Add/remove properties and children from category definition */
  ElementCategoryEnrich = 'element.category.enrich',
  /** Update element quantity record (auto-clear obsolete, set fallback expressions) */
  ElementQuantityAutoUpdate = 'element.quantity.autoUpdate',
  /** Set {@link IElement.selectedQuantity} via {@link selectElementQuantity} */
  ElementQuantitySelect = 'element.quantity.select',
  /** Exclude element from calculations (user visibility off) */
  ElementDeactivate = 'element.deactivate',
  /** Include element in calculations (user visibility on / new in proposal) */
  ElementActivate = 'element.activate',
  /** Soft-delete element in a proposal by setting {@link IElement.isDeleted}; baseline tree is untouched */
  ElementSoftDelete = 'element.softDelete',
  /** Apply a recipe structurally, replacing properties and children ({@link overwriteRecipe}) */
  ElementRecipeApply = 'element.recipe.apply',
  /** Detach recipe link while keeping recipe-sourced properties and children */
  ElementRecipeDetach = 'element.recipe.detach',
  /** Clear recipe link, recipe properties, and recipe children */
  ElementRecipeClear = 'element.recipe.clear',
  /** Point element at a different element category */
  ElementCategory = 'element.category',
  /** Add a new child element to a parent element */
  ElementAdd = 'element.add',
  /** Add a property to an element */
  ElementPropertyAdd = 'element.property.add',
  /** Remove a property from an element */
  ElementPropertyRemove = 'element.property.remove',
  /** Set {@link IElement.name} and {@link IElement.fallbackName} (shared across proposals) */
  ElementName = 'element.name',
  /** Re-point a CSV-import {@link IElement.origin} (e.g. inline→stored after "Save mapping") */
  ElementOrigin = 'element.origin',
  /**
   * Hard-delete an element from the project tree; affects baseline and all proposals.
   * Building versions are removed with {@link ProjectChangeType.VersionRemove}.
   */
  ElementRemove = 'element.remove',

  // ── Product element changes ─────────────────────────────────
  ProductElementCountResolved = 'productElement.count.resolved',
  ProductElementCountExpression = 'productElement.count.expression',

  ProductElementCountFormatted = 'productElement.count.formatted',
  ProductElementProductId = 'productElement.productId',
  /** Switch product element to another EPD / generic pair */
  ProductElementEpd = 'productElement.epd',
  /** Transport distance override (km) on {@link IProductElement.productOverrides} */
  ProductElementTransportOverride = 'productElement.override.transport',
  /** Waste/spill percent override on {@link IProductElement.productOverrides} */
  ProductElementWasteOverride = 'productElement.override.waste',
  /** Carbon/cost market percentile pair on {@link IProductElement.productOverrides} */
  ProductElementCarbonLevelOverride = 'productElement.override.carbonLevel',

  // ── Version changes ─────────────────────────────────────────
  /**
   * Add a new {@link Baseline} to the building — optionally based on an existing
   * version (duplicate). Separate from {@link ProjectChangeType.ElementAdd}, which only
   * ever adds children *inside* a version.
   */
  VersionAdd = 'version.add',
  /**
   * Delete a whole {@link Baseline} from the building, including its proposals and
   * actions. The immediately preceding version takes over as
   * {@link Project.active_version_id} when the removed one was active (the next version
   * if the first was deleted — see {@link removeBuildingVersion}).
   */
  VersionRemove = 'version.remove',
  /** Set {@link IProposal.resultsRecord} on a proposal in {@link Baseline.proposals} */
  ProposalResultsRecord = 'proposal.resultsRecord',
  VersionProducts = 'version.products',
  VersionResults = 'version.results',
  /** Rename a {@link Baseline} */
  VersionName = 'version.name',
  /** Replace {@link Baseline.imports} (CSV import subscriptions + discarded rows) */
  VersionImports = 'version.imports',
  /** Clear migrated {@link Baseline.proposals} and {@link Baseline.actions} (admin re-migration) */
  VersionClearProposals = 'version.clearProposals',
  /** Append a new {@link IProposal} to a version (name auto-deduped, palette index auto-picked) */
  ProposalAdd = 'proposal.add',
  /**
   * Duplicate an {@link IProposal}: clone its {@link Action}s onto new ids and insert
   * the copy after the source (DEV-3658). Never shares {@link IProposal.actionIds}
   * with the source (DEV-3268).
   */
  ProposalDuplicate = 'proposal.duplicate',
  /** Delete a {@link IProposal} from a version and orphaned actions it owned */
  ProposalDelete = 'proposal.delete',
  /** Partial-update one or more {@link IProposal} rows (name, color, active, etc.) */
  ProposalUpdate = 'proposal.update',
  /** Append a new {@link Action} on a {@link Baseline} and link it on a {@link IProposal} */
  ProposalAppendAction = 'proposal.appendAction',
  /** Reorder {@link IProposal} rows in a version by providing the desired id order */
  ProposalReorder = 'proposal.reorder',
  /** Rename an existing {@link Action} on a {@link Baseline} */
  ActionRename = 'action.rename',
  /** Delete an {@link Action} from a {@link Baseline} and unlink it from every {@link IProposal} */
  ActionDelete = 'action.delete',
}

/** How a property row is merged when applying category-driven structural changes */
export type ProjectChangeAction = 'add' | 'remove' | 'update' | 'replace';

/**
 * Discriminated union for all possible project changes.
 * Each type has a specific structure for type-safe handling.
 */
export type ProjectChangeMap = {
  // ── Element changes ─────────────────────────────────────────

  [ProjectChangeType.ElementPropertyResolved]: {
    type: ProjectChangeType.ElementPropertyResolved;
    elementId: string;
    propertyName: string;
    value: number;
  };
  [ProjectChangeType.ElementPropertyExpression]: {
    type: ProjectChangeType.ElementPropertyExpression;
    elementId: string;
    propertyName: string;
    expression: string;
  };
  [ProjectChangeType.ElementPropertyFormatted]: {
    type: ProjectChangeType.ElementPropertyFormatted;
    elementId: string;
    propertyName: string;
    formatted: string;
  };
  [ProjectChangeType.ElementPropertyValue]: {
    type: ProjectChangeType.ElementPropertyValue;
    elementId: string;
    propertyName: string;
    /** Mirrors {@link ExpressionValue.value}; `undefined` clears the override */
    overrideValue: number | undefined;
  };
  [ProjectChangeType.ElementQuantityResolved]: {
    type: ProjectChangeType.ElementQuantityResolved;
    elementId: string;
    quantityName: ElementQuantityName;
    value: number;
  };
  [ProjectChangeType.ElementQuantityExpression]: {
    type: ProjectChangeType.ElementQuantityExpression;
    elementId: string;
    quantityName: ElementQuantityName;
    expression: string;
  };
  [ProjectChangeType.ElementQuantityFallbackExpression]: {
    type: ProjectChangeType.ElementQuantityFallbackExpression;
    elementId: string;
    quantityName: ElementQuantityName;
    expression: string;
  };
  [ProjectChangeType.ElementQuantityFormatted]: {
    type: ProjectChangeType.ElementQuantityFormatted;
    elementId: string;
    quantityName: ElementQuantityName;
    formatted: string;
  };
  [ProjectChangeType.ElementQuantityValue]: {
    type: ProjectChangeType.ElementQuantityValue;
    elementId: string;
    quantityName: ElementQuantityName;
    /** Mirrors {@link ExpressionValue.value}; `undefined` clears the override */
    overrideValue: number | undefined;
  };
  [ProjectChangeType.ElementQuantityCount]: {
    type: ProjectChangeType.ElementQuantityCount;
    elementId: string;
    quantityName: ElementQuantityName;
    /** `undefined` clears the count so fallback takes effect (expression rows use {@link IElementQuantityExpressionProperty.fallbackCount}) */
    count: IElementQuantityProperty['count'];
  };
  [ProjectChangeType.ElementQuantityFallbackCount]: {
    type: ProjectChangeType.ElementQuantityFallbackCount;
    elementId: string;
    quantityName: ElementQuantityName;
    fallbackCount: ExpressionValue;
  };
  [ProjectChangeType.ElementPropertyFallbackCount]: {
    type: ProjectChangeType.ElementPropertyFallbackCount;
    elementId: string;
    propertyName: string;
    fallbackCount: IElementProperty['fallbackCount'];
  };
  [ProjectChangeType.ElementPropertyCount]: {
    type: ProjectChangeType.ElementPropertyCount;
    elementId: string;
    propertyName: string;
    count: IElementProperty['count'];
  };
  [ProjectChangeType.ElementPropertyRename]: {
    type: ProjectChangeType.ElementPropertyRename;
    elementId: string;
    /** Current property name used to identify the property before renaming */
    propertyName: string;
    name: string;
  };
  [ProjectChangeType.ElementDataTablePropertiesApply]: {
    type: ProjectChangeType.ElementDataTablePropertiesApply;
    elementId: string;
    dataTable: IDataTable;
  };
  [ProjectChangeType.ElementCategoryEnrich]: {
    type: ProjectChangeType.ElementCategoryEnrich;
    elementId: string;
    /** Properties added by category definition */
    addProperties: IElementProperty[];
    /** Property names removed by category definition */
    removePropertyNames: string[];
    /** Child elements added by category definition */
    addChildren: OneOfChildElements[];
    /** Child element IDs removed by category definition */
    removeChildIds: string[];
  };
  [ProjectChangeType.ElementQuantityAutoUpdate]: {
    type: ProjectChangeType.ElementQuantityAutoUpdate;
    elementId: string;
    /** The full updated quantity record to replace the existing one */
    quantity: IElement['quantity'];
  };
  [ProjectChangeType.ElementQuantitySelect]: {
    type: ProjectChangeType.ElementQuantitySelect;
    elementId: string;
    quantityName: 'auto' | ElementQuantityName | undefined;
  };
  [ProjectChangeType.ElementDeactivate]: {
    type: ProjectChangeType.ElementDeactivate;
    elementId: string;
  };
  [ProjectChangeType.ElementActivate]: {
    type: ProjectChangeType.ElementActivate;
    elementId: string;
  };
  [ProjectChangeType.ElementSoftDelete]: {
    type: ProjectChangeType.ElementSoftDelete;
    elementId: string;
  };
  [ProjectChangeType.ElementRecipeApply]: {
    type: ProjectChangeType.ElementRecipeApply;
    elementId: string;
    recipeId: RecipeID;
    /** Recipe payload applied via {@link overwriteRecipeForProposal} in the update handler */
    recipe: Recipe;
    /**
     * Structural overwrite strategy for the apply handler. Defaults to `'proposal'`
     * ({@link overwriteRecipeForProposal} — keeps baseline-only properties the new recipe omits,
     * required for proposal replay and per-proposal swaps, DEV-3293/DEV-3350). `'baseline'` uses the
     * clean-slate {@link overwriteRecipe} for direct baseline applies (recipe selector, no proposal).
     */
    overwriteMode?: 'baseline' | 'proposal';
    /**
     * Regenerated {@link IElement.fallbackName} to persist alongside the recipe structure
     * ({@link generateElementFallbackName}). Absent on legacy stored actions and on detach/clear.
     */
    fallbackName?: string;
    /**
     * Cached post-enrich {@link IElement} subtree as it appears in the proposal-resolved view.
     * Written back by the pipeline after each enrich run; consumed by
     * {@link resolveAndEnrichVersionForProposal} so {@link useResolvedVersion} can substitute
     * the subtree directly and skip the expensive category-enrich + auto-quantity walk for
     * recipe-applied subtrees on proposal switches.
     */
    resolvedElement?: IElement;
  };
  [ProjectChangeType.ElementRecipeDetach]: {
    type: ProjectChangeType.ElementRecipeDetach;
    elementId: string;
  };
  [ProjectChangeType.ElementRecipeClear]: {
    type: ProjectChangeType.ElementRecipeClear;
    elementId: string;
  };
  [ProjectChangeType.ElementCategory]: {
    type: ProjectChangeType.ElementCategory;
    elementId: string;
    categoryId: ElementCategoryID;
  };
  [ProjectChangeType.ElementAdd]: {
    type: ProjectChangeType.ElementAdd;
    /** Parent element OR building version that receives the new child */
    elementId: string;
    /**
     * When duplicating an element subtree under a proposal, set to the **source** root
     * element id so proposal actions targeting that subtree can be cloned onto the new ids
     * (see `collectRemappedProposalChangesForDuplicateSubtree`).
     */
    duplicateSubtreeSourceElementId?: string;
    /**
     * Mark the new root as created by this proposal ({@link IElement.createdInProposalId}).
     * Set by the proposal-scoped add wrapper and applied by the mutator after a copy's
     * provenance is stripped, so a duplicate made inside a proposal is owned by it while
     * the same duplicate made in baseline is not.
     */
    createdInProposalId?: ProposalId;
    /**
     * Factory partial used to create the child via {@link createElementFromPartial}.
     * Versions are never children — add those with {@link ProjectChangeType.VersionAdd}.
     */
    factory: Partial<OneOfChildFactoryElements>;
    /** Insertion placement among siblings (defaults to `'first'`) */
    placement?: IInsertSortPlacement;
    /** When set, the new child is placed relative to this sibling id (used with {@link IInsertSortPlacement} `'before'` / `'after'`) */
    siblingId?: string;
    /** Controls id regeneration for the factory subtree (defaults to `true`) */
    regenerateIds?: RegenerateIds;
    /** Whether the new element name should be deduplicated against siblings (defaults to `true`) */
    generateUniqueName?: boolean;
  };
  [ProjectChangeType.ElementPropertyAdd]: {
    type: ProjectChangeType.ElementPropertyAdd;
    elementId: string;
    /** Factory definition for the property to create */
    factory: IFactoryProperty;
  };
  [ProjectChangeType.ElementPropertyRemove]: {
    type: ProjectChangeType.ElementPropertyRemove;
    elementId: string;
    /** IDs of properties to remove */
    propertyIds: string[];
  };
  [ProjectChangeType.ElementName]: {
    type: ProjectChangeType.ElementName;
    elementId: string;
    name: string;
    fallbackName: string;
  };
  [ProjectChangeType.ElementOrigin]: {
    type: ProjectChangeType.ElementOrigin;
    elementId: string;
    /** Replacement provenance (e.g. inline snapshot re-pointed to a stored mapping). */
    origin: ElementOrigin;
  };
  [ProjectChangeType.ElementRemove]: {
    type: ProjectChangeType.ElementRemove;
    elementId: string;
  };

  // ── Product element changes ─────────────────────────────────

  [ProjectChangeType.ProductElementCountResolved]: {
    type: ProjectChangeType.ProductElementCountResolved;
    elementId: string;
    value: number;
  };
  [ProjectChangeType.ProductElementCountExpression]: {
    type: ProjectChangeType.ProductElementCountExpression;
    elementId: string;
    expression: string;
  };
  [ProjectChangeType.ProductElementCountFormatted]: {
    type: ProjectChangeType.ProductElementCountFormatted;
    elementId: string;
    formatted: string;
  };
  [ProjectChangeType.ProductElementProductId]: {
    type: ProjectChangeType.ProductElementProductId;
    elementId: string;
    productId: string;
  };
  [ProjectChangeType.ProductElementEpd]: {
    type: ProjectChangeType.ProductElementEpd;
    elementId: string;
    productId: ProductID;
    genericProductId: ProductID | undefined;
  };
  [ProjectChangeType.ProductElementTransportOverride]: {
    type: ProjectChangeType.ProductElementTransportOverride;
    elementId: string;
    /** `undefined` removes the transport override */
    value: number | undefined;
  };
  [ProjectChangeType.ProductElementWasteOverride]: {
    type: ProjectChangeType.ProductElementWasteOverride;
    elementId: string;
    /** Waste percent 0–100; `undefined` removes the override */
    value: number | undefined;
  };
  [ProjectChangeType.ProductElementCarbonLevelOverride]: {
    type: ProjectChangeType.ProductElementCarbonLevelOverride;
    elementId: string;
    /** Carbon market percentile; `undefined` clears carbon and cost overrides */
    percentile: number | undefined;
  };

  // ── Version changes ─────────────────────────────────────────

  [ProjectChangeType.VersionAdd]: {
    type: ProjectChangeType.VersionAdd;
    /**
     * Id the new version gets. Always caller-provided so navigation, selection and
     * {@link ApplyProjectChangesOptions.activeVersionId} can reference the version before
     * the change is applied.
     */
    versionId: string;
    /**
     * Version to base the new one on (e.g. an existing version when duplicating). Child ids
     * are always regenerated, so the source keeps its own tree.
     */
    factory?: Partial<IFactoryVersion>;
    /** Source name used only by Duplicate; the mutator resolves its first free numeric suffix. */
    duplicateNameBase?: string;
  };
  [ProjectChangeType.VersionRemove]: {
    type: ProjectChangeType.VersionRemove;
    versionId: string;
  };
  [ProjectChangeType.ProposalResultsRecord]: {
    type: ProjectChangeType.ProposalResultsRecord;
    versionId: string;
    proposalId: ProposalId;
    resultsRecord: ResultsRecord;
  };
  [ProjectChangeType.VersionProducts]: {
    type: ProjectChangeType.VersionProducts;
    versionId: string;
    products: ProductRecord;
  };
  [ProjectChangeType.VersionResults]: {
    type: ProjectChangeType.VersionResults;
    versionId: string;
    results: Results;
  };
  [ProjectChangeType.VersionName]: {
    type: ProjectChangeType.VersionName;
    versionId: string;
    name: string;
  };
  [ProjectChangeType.VersionImports]: {
    type: ProjectChangeType.VersionImports;
    versionId: string;
    imports: NonNullable<Baseline['imports']>;
  };
  [ProjectChangeType.VersionClearProposals]: {
    type: ProjectChangeType.VersionClearProposals;
    versionId: string;
  };
  [ProjectChangeType.ProposalAdd]: {
    type: ProjectChangeType.ProposalAdd;
    versionId: string;
    /** Pre-generated id used when UI selection must be ready before the optimistic store write. */
    proposalId?: ProposalId;
    /** Optional partial fields for the new proposal (name, colorIndex, ...). When omitted, defaults from {@link createProposal} are used. */
    defaults?: Partial<IProposal>;
  };
  [ProjectChangeType.ProposalDuplicate]: {
    type: ProjectChangeType.ProposalDuplicate;
    versionId: string;
    /** Source {@link IProposal.id} to clone (actions, results, report fields). */
    proposalId: ProposalId;
  };
  [ProjectChangeType.ProposalDelete]: {
    type: ProjectChangeType.ProposalDelete;
    versionId: string;
    proposalId: ProposalId;
  };
  [ProjectChangeType.ProposalUpdate]: {
    type: ProjectChangeType.ProposalUpdate;
    versionId: string;
    updates: SemiPartial<IProposal, 'id'>[];
  };
  [ProjectChangeType.ProposalAppendAction]: {
    type: ProjectChangeType.ProposalAppendAction;
    versionId: string;
    proposalId: ProposalId;
    /** Inner project changes that will be condensed into a single new {@link Action}. */
    changes: ProjectChange[];
  };
  [ProjectChangeType.ProposalReorder]: {
    type: ProjectChangeType.ProposalReorder;
    versionId: string;
    /** Desired order of proposal ids — all existing ids must be present */
    orderedIds: ProposalId[];
  };
  [ProjectChangeType.ActionRename]: {
    type: ProjectChangeType.ActionRename;
    versionId: string;
    actionId: string;
    name: string;
  };
  [ProjectChangeType.ActionDelete]: {
    type: ProjectChangeType.ActionDelete;
    versionId: string;
    actionId: string;
  };
};

// ── Sub-type discriminants ────────────────────────────────────

/** Change types targeting {@link IElement} nodes */
export type ElementChangeType =
  | ProjectChangeType.ElementPropertyResolved
  | ProjectChangeType.ElementPropertyExpression
  | ProjectChangeType.ElementPropertyFormatted
  | ProjectChangeType.ElementPropertyValue
  | ProjectChangeType.ElementQuantityResolved
  | ProjectChangeType.ElementQuantityExpression
  | ProjectChangeType.ElementQuantityFallbackExpression
  | ProjectChangeType.ElementQuantityFormatted
  | ProjectChangeType.ElementQuantityValue
  | ProjectChangeType.ElementQuantityCount
  | ProjectChangeType.ElementQuantityFallbackCount
  | ProjectChangeType.ElementPropertyFallbackCount
  | ProjectChangeType.ElementPropertyCount
  | ProjectChangeType.ElementPropertyRename
  | ProjectChangeType.ElementDataTablePropertiesApply
  | ProjectChangeType.ElementCategoryEnrich
  | ProjectChangeType.ElementQuantityAutoUpdate
  | ProjectChangeType.ElementQuantitySelect
  | ProjectChangeType.ElementDeactivate
  | ProjectChangeType.ElementActivate
  | ProjectChangeType.ElementSoftDelete
  | ProjectChangeType.ElementRecipeApply
  | ProjectChangeType.ElementRecipeDetach
  | ProjectChangeType.ElementRecipeClear
  | ProjectChangeType.ElementCategory
  | ProjectChangeType.ElementAdd
  | ProjectChangeType.ElementPropertyAdd
  | ProjectChangeType.ElementPropertyRemove
  | ProjectChangeType.ElementName
  | ProjectChangeType.ElementOrigin
  | ProjectChangeType.ElementRemove;

/** Change types targeting {@link IProductElement} nodes */
export type ProductElementChangeType =
  | ProjectChangeType.ProductElementCountResolved
  | ProjectChangeType.ProductElementCountExpression
  | ProjectChangeType.ProductElementCountFormatted
  | ProjectChangeType.ProductElementProductId
  | ProjectChangeType.ProductElementEpd
  | ProjectChangeType.ProductElementTransportOverride
  | ProjectChangeType.ProductElementWasteOverride
  | ProjectChangeType.ProductElementCarbonLevelOverride;

/** Change types targeting {@link Baseline} */
export type VersionChangeType =
  | ProjectChangeType.VersionAdd
  | ProjectChangeType.VersionRemove
  | ProjectChangeType.ProposalResultsRecord
  | ProjectChangeType.VersionProducts
  | ProjectChangeType.VersionResults
  | ProjectChangeType.VersionName
  | ProjectChangeType.VersionImports
  | ProjectChangeType.VersionClearProposals
  | ProjectChangeType.ProposalAdd
  | ProjectChangeType.ProposalDuplicate
  | ProjectChangeType.ProposalDelete
  | ProjectChangeType.ProposalUpdate
  | ProjectChangeType.ProposalAppendAction
  | ProjectChangeType.ProposalReorder
  | ProjectChangeType.ActionRename
  | ProjectChangeType.ActionDelete;

/** Runtime list of {@link VersionChangeType} — keep in lockstep with the union above. */
export const versionChangeTypes: readonly VersionChangeType[] = [
  ProjectChangeType.VersionAdd,
  ProjectChangeType.VersionRemove,
  ProjectChangeType.ProposalResultsRecord,
  ProjectChangeType.VersionProducts,
  ProjectChangeType.VersionResults,
  ProjectChangeType.VersionName,
  ProjectChangeType.VersionImports,
  ProjectChangeType.VersionClearProposals,
  ProjectChangeType.ProposalAdd,
  ProjectChangeType.ProposalDuplicate,
  ProjectChangeType.ProposalDelete,
  ProjectChangeType.ProposalUpdate,
  ProjectChangeType.ProposalAppendAction,
  ProjectChangeType.ProposalReorder,
  ProjectChangeType.ActionRename,
  ProjectChangeType.ActionDelete,
];

/** All non-version change types (elements + product elements) */
export type NonVersionChangeType = Exclude<
  ProjectChangeType,
  VersionChangeType
>;

/** Runtime array of all non-version change types (for type guards) */
export const nonVersionChangeTypes: NonVersionChangeType[] = Object.values(
  ProjectChangeType,
).filter((type) => !isOneOf(versionChangeTypes, type));

/** Why a stored proposal change was deactivated (DEV-3640). */
export type InactiveChangeReason =
  | 'stale-element'
  | 'stale-child'
  | 'stale-property'
  | 'stale-property-name'
  | 'stale-quantity-name';

/**
 * Marks a stored proposal change as permanently skipped. Set when a baseline edit
 * retroactively orphans a positional reference the change targets (DEV-3640).
 * Never cleared — positional ids are reused, so a revived id may belong to a
 * different element and the change must not reactivate.
 */
export type InactiveChangeMeta = {
  reason: InactiveChangeReason;
  /** ISO 8601 timestamp for when the change was deactivated */
  at: string;
};

export type ProjectChange = ProjectChangeMap[keyof ProjectChangeMap] & {
  inactive?: InactiveChangeMeta;
};

export type VersionChange = ProjectChangeMap[VersionChangeType];

export type ElementChange = ProjectChangeMap[ElementChangeType];

export type ProductElementChange = ProjectChangeMap[ProductElementChangeType];

/** Any element-level or product-element-level change */
export type NonVersionChange = ProjectChangeMap[NonVersionChangeType];

/**
 * Grouped project changes for one user operation (e.g. an entry in a proposal action list).
 * {@link Action.name} is optional display text; {@link ActionId} in the proposals model shares this `id` string space.
 */
export type Action = {
  id: string;
  name?: string;
  changes: ProjectChange[];
};
